import { useMemo } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { faSync } from '@fortawesome/pro-regular-svg-icons';
import { QuickBooksTransactionSyncStatus } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/constants';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import {
    transformMultiselectToReadable,
    transformQuickBooksTransactionStatusToReadable,
} from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import { zeroQuickBooksTransactionStatusSelected } from 'components/filter/validations';
import * as Form from 'components/form';
import { identity } from 'lodash';

interface QuickBooksTransactionStatusFormValues
    extends FilterFormValue<QuickBooksTransactionSyncStatus[]> {}

export const filterKey: ServerKey = 'quickBooksTransactionStatus';

export const Icon: FilterIcon = faSync;

export const Filter: ContentFilterComponent = () => {
    const { defaultValue, ...rest } = useFilter<QuickBooksTransactionSyncStatus[]>();
    const methods = Form.useForm<QuickBooksTransactionStatusFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });
    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>Sync status</Form.Label>
                            <Form.Combobox
                                name={'value'}
                                control={control}
                                rules={{
                                    validate: zeroQuickBooksTransactionStatusSelected,
                                }}
                            >
                                <FilterComboboxItems>
                                    <Combobox.Item value="ready_to_sync">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Ready to sync</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="do_not_sync">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Do not sync</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="synced">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Synced</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="issues">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Sync issue</Combobox.ItemText>
                                    </Combobox.Item>
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                    </FilterContainer>
                    <FilterSubmit />
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<QuickBooksTransactionSyncStatus[]>();
    const value = currentFilter?.value ?? [];

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: transformQuickBooksTransactionStatusToReadable,
            })}
        </ReadableFilterValue>
    );
};

export const Label: LabelFilterComponent = () => 'Sync status';

export const downstream: DownstreamFilterTransform<QuickBooksTransactionSyncStatus> = identity;

export const upstream: UpstreamFilterTransform<QuickBooksTransactionSyncStatus> = (
    filter: FilterValue,
) =>
    injectEquals({
        value: filter.value,
    });
